import { useEffect, useRef, type ReactNode } from "react";
import { Animated, Pressable, ScrollView } from "react-native";
import { createPortal } from "react-dom";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { Icon } from "../Icon";

interface RightDrawerProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  borderRadius?: number;
  scrollable?: boolean;
}

/**
 * Right-side drawer overlay component for web.
 * Slides in from the right with a semi-transparent backdrop.
 */
export function RightDrawer({
  visible,
  onClose,
  title,
  children,
  footer,
  width = sizes.webDrawerWidth,
  borderRadius,
  scrollable = true,
}: RightDrawerProps) {
  const { theme } = useUnistyles();
  const slideAnim = useRef(new Animated.Value(width)).current;
  const backdropAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      // Slide in
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(backdropAnim, {
          toValue: 1,
          duration: 250,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      // Slide out
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: width,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(backdropAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, slideAnim, backdropAnim, width]);

  if (!visible) {
    return null;
  }

  const drawerContent = (
    <View style={styles.overlay}>
      {/* Backdrop */}
      <Pressable onPress={onClose} style={styles.backdropPressable}>
        <Animated.View
          style={[
            styles.backdrop,
            {
              opacity: backdropAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 0.4],
              }),
            },
          ]}
        />
      </Pressable>

      {/* Drawer */}
      <Animated.View
        style={[
          styles.drawer,
          {
            width,
            backgroundColor: theme.colors.surface,
            transform: [{ translateX: slideAnim }],
            borderRadius: borderRadius ?? theme.spacing["4xl"],
          },
        ]}
      >
        {/* Header */}
        {title != null && (
          <>
            <View flexDirection="row" justifyContent="space-between" alignItems="center" px="6xl" py="5xl">
              <Text textStyle="lg/semibold" color="text">
                {title}
              </Text>
              <Pressable
                onPress={onClose}
                style={[
                  styles.closeButton,
                  {
                    borderColor: theme.colors.border,
                  },
                ]}
              >
                <Icon name="close" size={28} color={theme.colors.textMuted} />
              </Pressable>
            </View>

            {/* Separator */}
            <View height={1} bg="border" mx="6xl" />
          </>
        )}

        {/* Content */}
        {scrollable ?
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
          >
            {children}
          </ScrollView>
        : <View style={[styles.content, styles.contentContainer]}>{children}</View>}

        {/* Footer */}
        {footer && (
          <>
            <View height={1} bg="border" mx="6xl" />
            <View px="6xl" py="5xl">
              {footer}
            </View>
          </>
        )}
      </Animated.View>
    </View>
  );

  // Use portal to render drawer at document.body level for full viewport coverage
  if (typeof document !== "undefined") {
    return createPortal(drawerContent, document.body);
  }

  return drawerContent;
}

const styles = StyleSheet.create((theme) => ({
  overlay: {
    // "fixed" is valid on web but not in RN types
    position: "fixed" as "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  },
  backdropPressable: {
    position: "fixed" as "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  backdrop: {
    flex: 1,
    backgroundColor: theme.colors.black.default,
  },
  drawer: {
    position: "fixed" as "absolute",
    top: 0,
    right: 0,
    bottom: 0,
  },
  closeButton: {
    width: theme.spacing["9xl"],
    height: theme.spacing["9xl"],
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: theme.spacing["6xl"],
    paddingVertical: theme.spacing["6xl"],
  },
}));
